import React from "react";
import { ContentState, Editor, EditorState } from "draft-js";
import _ from "lodash";
import CreatableSelect from 'react-select/creatable';
import Select, { components } from "react-select";
import { API } from "aws-amplify";
import { toast } from "react-toastify";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import InputCell from "@/common/form/InputCell";
import {
    comparisonOperators,
    surveyActionComparisonFields,
    surveyReviewTargetTypeOptionsWithoutQuestion
} from "@/utils/utils";
import {isExpandedSentimentTypesEnabled} from "@/shared/access";

const sentimentDropdownOptions = [
    { value: 'POSITIVE', label: 'Positive' },
    { value: 'NEGATIVE', label: 'Negative' },
    { value: 'NEUTRAL',  label: 'Neutral'  },
    { value: 'MIXED',    label: 'Mixed'    },
    { value: 'OTHER',    label: 'Other'    },
    { value: 'NONE',     label: 'None'     },
];

const sentimentMultiSelectStyles = {
    valueContainer: (base) => ({ ...base, flexWrap: 'nowrap', overflow: 'hidden' }),
};

const sentimentAriaLiveMessages = {
    guidance: (props) => {
        if (props.context === 'menu') {
            return 'Expanded. Use Up and Down to choose options, press Enter to select, press Escape to exit the menu.';
        }
        return '';
    },
    onChange: () => '',
};

const SentimentCheckboxOption = ({ children, isSelected, isFocused, innerProps, innerRef }) => (
    <div ref={innerRef} {...innerProps} style={{
        display: 'flex', alignItems: 'center', padding: '2px 8px',
        cursor: 'pointer',
        backgroundColor: isFocused ? 'var(--colorTintTranslucent)' : 'transparent',
        outline: isFocused ? '2px solid var(--colorTint)' : 'none',
        outlineOffset: -2,
        borderRadius: 6,
    }}>
        <Checkbox
            checked={isSelected}
            onChange={() => {}}
            size="small"
            style={{ padding: 4 }}
            aria-hidden={true}
            tabIndex={-1}
        />
        <span style={{ color: 'var(--colorTitle)' }}>{children}</span>
    </div>
);

const SentimentValueContainer = ({ children, getValue, ...props }) => {
    const selected = getValue();
    return (
        <components.ValueContainer getValue={getValue} {...props}>
            {selected.length > 0 && (
                <span style={{ color: 'var(--colorTitle)', paddingLeft: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {selected.map(v => v.label).join(', ')}
                </span>
            )}
            {React.Children.map(children, child =>
                child && !child.props?.data ? child : null
            )}
        </components.ValueContainer>
    );
};

const SentimentClearIndicator = ({ innerProps, ...props }) => (
    <components.ClearIndicator
        {...props}
        innerProps={{
            ...innerProps,
            'aria-hidden': undefined,
            role: 'button',
            tabIndex: 0,
            'aria-label': 'Clear all selected sentiment values',
            onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    innerProps.onMouseDown?.(e);
                }
            }
        }}
    />
);

const EMPTY_VALUE = "-";

//For follow up questions.  Very similar to edit question used in survey creation.
export default function AddEditQuestion({ existingQuestion, question, onUpdate, disabled, user, taskTypes, onCancel, onSuccess }) {
    const [editorState, setEditorState] = React.useState();
    const [inputValue, setInputValue] = React.useState([]);
    const [multiSelectValue, setMultiSelectValue] = React.useState([]);
    const [reviewCriterias, setReviewCriterias] = React.useState([]);
    const savedFieldValuesRef = React.useRef({});
    const savedOperatorValuesRef = React.useRef({});
    const [sentimentMenuOpen, setSentimentMenuOpen] = React.useState({});
    const savedTypeStateRef = React.useRef({});
    const [states, setStates] = React.useState([]);
    let domEditor;
    const setDomEditorRef = ref => domEditor = ref;

    React.useEffect(() => {
        if (question) {
            let initEditorState = ContentState.createFromText(question.prompt ? question.prompt : "");
            setEditorState(EditorState.createWithContent(initEditorState));
            if (question.surveyQuestionReviewCriteria == null) {
                setReviewCriterias([]);
            } else {
                const initCriterias = question.surveyQuestionReviewCriteria;
                if (isExpandedSentimentTypesEnabled()) {
                    initCriterias.forEach(c => {
                        if (c.comparisonField === "SENTIMENT" && c.comparisonValue) {
                            c.comparisonValue = c.comparisonValue.split(",").map(v => v.trim().toUpperCase()).join(",");
                        }
                    });
                }
                setReviewCriterias(initCriterias);
                _.forEach(initCriterias, function (criteria, j) {
                    multiSelectValue[j] = [];
                    if (criteria.comparisonValue != null && criteria.comparisonValue.length > 0 &&
                        (criteria.comparison === "IN_LIST" || criteria.comparison === "NOT_IN_LIST")) {
                        if (isExpandedSentimentTypesEnabled() && criteria.comparisonField === "SENTIMENT") {
                            var sentValues = criteria.comparisonValue.split(",");
                            multiSelectValue[j] = sentValues.map(v => {
                                const opt = sentimentDropdownOptions.find(o => o.value === v.trim());
                                return opt ? opt : { label: v.trim(), value: v.trim() };
                            });
                        } else {
                            var values = criteria.comparisonValue.split(",");
                            var formatted = [];
                            for (var i = 0; i < values.length; i++) {
                                formatted.push(createOption(values[i]));
                            }
                            multiSelectValue[j] = formatted;
                        }
                    }
                });
                setMultiSelectValue(multiSelectValue);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [question]);

    React.useEffect(() => {
        joinValues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [multiSelectValue])

    function editorContainerClick() {
        domEditor.focus();
    }

    function comparisonFieldOpts(questionType) {

        if (questionType === "SCALE" || questionType === "CHOICE") {
            return [
                { value: 'NORMALIZED_ANSWER', label: 'ANSWER' }
            ];
        } else {
            return surveyActionComparisonFields;
        }
    }

    function comparisonOpts(questionType) {

        if (questionType === "SCALE") {
            return [
                { value: 'EQUAL', label: '=' },
                { value: 'GREATER_THAN', label: '>' },
                { value: 'GREATER_THAN_EQUALS', label: '>=' },
                { value: 'LESS_THAN', label: '<' },
                { value: 'LESS_THAN_EQUALS', label: '<=' }
            ];
        } else if (questionType === "CHOICE") {
            return [
                { value: 'EQUAL', label: '=' },
                { value: 'CONTAINS', label: 'Contains' },
                { value: 'DOES_NOT_CONTAIN', label: 'Does Not Contain' },
                { value: 'IN_LIST', label: 'In List' },
                { value: 'NOT_IN_LIST', label: 'Not In List' }
            ];
        } else {
            return [
                { value: 'EQUAL', label: '=' },
                { value: 'CONTAINS', label: 'Contains' },
                { value: 'DOES_NOT_CONTAIN', label: 'Does Not Contain' },
                { value: 'IN_LIST', label: 'In List' },
                { value: 'NOT_IN_LIST', label: 'Not In List' }
            ];
        }
    }

    const canSubmit = () => {
        if (question.prompt == null || question.prompt === "") {
            return false;
        }
        return true;
    }

    const recalculateOrder = (options) => {
        const affectedOptions = []
        options.forEach((option, index) => {
            if (option.optionOrder !== index )  {
                option.optionOrder = index;
            }
        })

        return affectedOptions;
    }

    const saveQuestion = () => {
        if (!canSubmit()) {
            return;
        }
        question.followUp = true;
        if(question.type === "CHOICE" && question.options != undefined) {
            recalculateOrder(question.options);
        }

        if (existingQuestion) {
            API.put('Core', '/api/v1/survey/followups/' + existingQuestion.id, { body: question })
                .then(response => {
                    toast.success("Follow up updated!", { position: "top-center" });
                    onSuccess(response);
                },
                    error => {
                        toast.error("Could not update follow up: " + error.response.data.message, { position: "top-center" });
                    }
                );
        }
        else {
            API.post('Core', '/api/v1/survey/followups', { body: question })
                .then(response => {
                    toast.success("Follow up Created!", { position: "top-center" });
                    onSuccess(response);
                },
                    error => {
                        toast.error("Could not create follow up: " + error.response.data.message, { position: "top-center" });
                    }
                );
        }
    }

    const updateEditorState = (newEditorState) => {
        setEditorState(newEditorState);
    }

    const blurEditor = () => {
        question.prompt = editorState.getCurrentContent().getPlainText();
        //onUpdate(question);
    }

    const setQuestionType = (obj, action) => {
        // eslint-disable-next-line default-case
        switch (action.action) {
            case "select-option": {
                const prevType = question.type;
                question.type = obj.value;
                if (question.surveyQuestionReviewCriteria) {
                    const validOps = obj.value === "SCALE"
                        ? ["EQUAL", "GREATER_THAN", "GREATER_THAN_EQUALS", "LESS_THAN", "LESS_THAN_EQUALS"]
                        : ["EQUAL", "CONTAINS", "DOES_NOT_CONTAIN", "IN_LIST", "NOT_IN_LIST"];
                    const newMultiSelect = [...multiSelectValue];
                    const newSavedTypeState = { ...savedTypeStateRef.current };
                    const newFieldValues = {};
                    question.surveyQuestionReviewCriteria.forEach((c, i) => {
                        // Save current criteria state under the previous type, including field switch save
                        newSavedTypeState[`${i}_${prevType}`] = {
                            comparison: c.comparison,
                            comparisonValue: c.comparisonValue,
                            comparisonField: c.comparisonField,
                            noteworthy: c.noteworthy,
                            multiSelect: [...(multiSelectValue[i] || [])],
                            fieldSave: savedFieldValuesRef.current[i]
                        };
                        // Restore saved state for the new type if it exists
                        const savedForNewType = newSavedTypeState[`${i}_${obj.value}`];
                        if (savedForNewType) {
                            c.comparison = savedForNewType.comparison;
                            c.comparisonValue = savedForNewType.comparisonValue;
                            c.comparisonField = savedForNewType.comparisonField;
                            c.noteworthy = savedForNewType.noteworthy;
                            newMultiSelect[i] = savedForNewType.multiSelect;
                            if (savedForNewType.fieldSave) newFieldValues[i] = savedForNewType.fieldSave;
                            delete newSavedTypeState[`${i}_${obj.value}`];
                        } else {
                            // No saved state for new type — start fresh
                            c.comparisonValue = "";
                            newMultiSelect[i] = [];
                            if ((obj.value === "SCALE" || obj.value === "CHOICE") && c.comparisonField === "SENTIMENT") {
                                c.comparisonField = "NORMALIZED_ANSWER";
                                c.noteworthy = false;
                            }
                            if (c.comparison && !validOps.includes(c.comparison)) {
                                c.comparison = "EQUAL";
                            }
                        }
                    });
                    savedTypeStateRef.current = newSavedTypeState;
                    savedFieldValuesRef.current = newFieldValues;
                    savedOperatorValuesRef.current = {};
                    setSentimentMenuOpen({});
                    setMultiSelectValue(newMultiSelect);
                    setReviewCriterias([...question.surveyQuestionReviewCriteria]);
                }
                onUpdate(question);
                break;
            }
        }
        setStates([]);
    }

    const setQuestionSubjectRole = (obj, action) => {
        // eslint-disable-next-line default-case
        switch (action.action) {
            case "select-option":
                question.subjectRole = obj.value;
                onUpdate(question);
                break;
        }
        setStates([]);
    }

    const setTargetTaskType = (idx, obj, action) => {
        let ttIdx = _.findIndex(taskTypes, function (tt) {
            return tt.id === obj.value;
        })
        const newCriterias = [...reviewCriterias];
        newCriterias[idx].taskType = taskTypes[ttIdx];
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const setQuestionReviewCriteriaTargetType = (idx, obj, action) => {
        const newCriterias = [...reviewCriterias];
        newCriterias[idx].targetType = obj.value;
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const onReviewSelectValueChange = (idx, obj) => {
        const newCriterias = [...reviewCriterias];
        newCriterias[idx].comparisonValue = obj.value;
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }


    const setQuestionReviewCriteriaComparisonField = (idx, obj, action) => {
        const newCriterias = [...reviewCriterias];
        const prevField = newCriterias[idx].comparisonField;
        newCriterias[idx].comparisonField = obj.value;
        if (obj.value !== "SENTIMENT") {
            newCriterias[idx].noteworthy = false;
        }
        if (prevField !== obj.value) {
            const newMultiSelect = [...multiSelectValue];
            const savedEntry = savedFieldValuesRef.current[idx] || {};
            if (obj.value === "SENTIMENT") {
                // Save current non-SENTIMENT value, restore previously saved SENTIMENT value
                const sentimentSaved = savedEntry.sentiment;
                savedFieldValuesRef.current = {
                    ...savedFieldValuesRef.current,
                    [idx]: { ...savedEntry, nonSentiment: { comparisonValue: newCriterias[idx].comparisonValue, multiSelect: [...(multiSelectValue[idx] || [])] } }
                };
                if (sentimentSaved !== undefined) {
                    newCriterias[idx].comparisonValue = sentimentSaved.comparisonValue;
                    newMultiSelect[idx] = sentimentSaved.multiSelect;
                } else {
                    newCriterias[idx].comparisonValue = "";
                    newMultiSelect[idx] = [];
                }
            } else if (prevField === "SENTIMENT") {
                // Save current SENTIMENT value, restore previously saved non-SENTIMENT value
                const nonSentimentSaved = savedEntry.nonSentiment;
                savedFieldValuesRef.current = {
                    ...savedFieldValuesRef.current,
                    [idx]: { ...savedEntry, sentiment: { comparisonValue: newCriterias[idx].comparisonValue, multiSelect: [...(multiSelectValue[idx] || [])] } }
                };
                if (nonSentimentSaved !== undefined) {
                    newCriterias[idx].comparisonValue = nonSentimentSaved.comparisonValue;
                    newMultiSelect[idx] = nonSentimentSaved.multiSelect;
                } else {
                    newCriterias[idx].comparisonValue = "";
                    newMultiSelect[idx] = [];
                }
            } else {
                newMultiSelect[idx] = [];
            }
            setMultiSelectValue(newMultiSelect);
        }
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const handleNoteworthyChange = (idx, checked) => {
        const newCriterias = [...reviewCriterias];
        newCriterias[idx].noteworthy = checked;
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const getSentimentOperatorGroup = (comparison) =>
        (comparison === "CONTAINS" || comparison === "DOES_NOT_CONTAIN") ? "text" : "dropdown";

    const setQuestionReviewCriteriaComparison = (idx, obj, action) => {
        const newCriterias = [...reviewCriterias];
        const prevComparison = newCriterias[idx].comparison;
        newCriterias[idx].comparison = obj.value;
        if (newCriterias[idx].comparisonField === "SENTIMENT" && prevComparison !== obj.value) {
            const prevGroup = getSentimentOperatorGroup(prevComparison);
            const newGroup = getSentimentOperatorGroup(obj.value);
            if (prevGroup !== newGroup) {
                const newMultiSelect = [...multiSelectValue];
                const saveKey = `${idx}_${prevGroup}`;
                const restoreKey = `${idx}_${newGroup}`;
                savedOperatorValuesRef.current = {
                    ...savedOperatorValuesRef.current,
                    [saveKey]: { comparisonValue: newCriterias[idx].comparisonValue, multiSelect: [...(multiSelectValue[idx] || [])] }
                };
                const saved = savedOperatorValuesRef.current[restoreKey];
                if (saved !== undefined) {
                    newCriterias[idx].comparisonValue = saved.comparisonValue;
                    newMultiSelect[idx] = saved.multiSelect;
                    const n = { ...savedOperatorValuesRef.current };
                    delete n[restoreKey];
                    savedOperatorValuesRef.current = n;
                } else {
                    newCriterias[idx].comparisonValue = "";
                    newMultiSelect[idx] = [];
                }
                setMultiSelectValue(newMultiSelect);
            }
        }
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const onReviewValueChange = (idx, obj) => {

        const newCriterias = [...reviewCriterias];
        newCriterias[idx].comparisonValue = obj.target.value;
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const components = {
        DropdownIndicator: null,
    };
    const handleChange = (idx, value, actionMeta) => {
        console.group('Value Changed');
        console.log(value);
        console.log(`action: ${actionMeta.action}`);
        console.groupEnd();
        if (value != null) {
            let newMultiSelect = [...multiSelectValue]
            newMultiSelect[idx] = value;
            setMultiSelectValue(newMultiSelect);
        } else {
            let newMultiSelect = [...multiSelectValue]
            newMultiSelect[idx] = [];
            setMultiSelectValue(newMultiSelect);
        }
    };
    const handleInputChange = (idx, newVal) => {
        let newInValue = [...inputValue]
        newInValue[idx] = newVal;
        setInputValue(newInValue);
    };
    const handleKeyDown = (idx, event) => {
        if (!inputValue) return;
        // eslint-disable-next-line default-case
        switch (event.key) {
            case 'Enter':
            case 'Tab':
                let newMulti = [...multiSelectValue];
                console.log(newMulti);
                if (multiSelectValue[idx] == null) {
                    multiSelectValue[idx] = [];
                }
                newMulti[idx] = multiSelectValue[idx].concat(createOption(inputValue[idx]));
                setMultiSelectValue(newMulti)
                console.group('Value Added');
                console.log(multiSelectValue);
                console.groupEnd();
                inputValue[idx] = '';
                setInputValue(inputValue);
                event.preventDefault();
        }
    };

    const createOption = (label) => ({
        label,
        value: label,
    });

    const joinValues = () => {
        for (var i = 0; i < multiSelectValue.length; i++) {
            if (multiSelectValue[i].length > 0) {
                var valuesOnly = [];
                for (var j = 0; j < multiSelectValue[i].length; j++) {
                    valuesOnly.push(multiSelectValue[i][j].value);
                }
                if (reviewCriterias[i] != null) {
                    reviewCriterias[i].comparisonValue = valuesOnly.join(',');
                    setReviewCriterias(reviewCriterias);
                    question.surveyQuestionReviewCriteria = reviewCriterias;
                    onUpdate(question);
                }
            }
        }

    }

    const onInputChange = (key, e) => {
        question[key] = e.target.value;
        onUpdate(question);
    }

    const onNameChange = (e) => {
        question.name = e.target.value;
        onUpdate(question);
    }

    const onOptChange = (idx, e) => {
        question.options[idx].option = e.target.value;
        question.options[idx].optionOrder = idx;
        onUpdate(question);
    }

    const onShortOptChange = (idx, e) => {
        question.options[idx].shortOption = e.target.value;
        onUpdate(question);
    }

    const addOpt = () => {
        if (!question.options) {
            question.options = [];
        }
        question.options.push({
            id: null,
            languagesTranslations: null,
            option: null,
            optionOrder: question.options.length + 1,
            shortOption:null,
        });
        onUpdate(question);
        setStates([]);
    }

    const removeOpt = (idx) => {
        question.options.sort((a, b) => a.optionOrder - b.optionOrder);
        question.options.splice(idx, 1);
        onUpdate(question);
        setStates([]);
    }

    const removeCriteria = (idx) => {
        multiSelectValue.splice(idx, 1);
        setMultiSelectValue(multiSelectValue);

        inputValue.splice(idx, 1);
        setInputValue(inputValue);

        const newCriterias = [...reviewCriterias];
        newCriterias.splice(idx, 1);
        setReviewCriterias([].concat(newCriterias));
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    const addCriteria = () => {
        let criterias = reviewCriterias;
        if (!criterias) {
            criterias = [];
        }
        setReviewCriterias(criterias.concat({
            comparisonValue: "" // set this property as a way of defaulting to enabled
        }));
        multiSelectValue.push([])
        setMultiSelectValue(multiSelectValue);
        inputValue.push("")
        setInputValue(inputValue);
    }

    const onFollowUpQuestionUpdate = (criteria, idx, fuQuestion) => {
        const newCriterias = [...reviewCriterias];
        criteria.followUpQuestion = fuQuestion
        newCriterias[idx] = criteria;
        setReviewCriterias(newCriterias);
        question.surveyQuestionReviewCriteria = newCriterias;
        onUpdate(question);
    }

    if (!editorState) {
        return null;
    }

    const typeOptions = [{ value: EMPTY_VALUE, label: EMPTY_VALUE }, { value: "SCALE", label: "Scale" }, {
        value: "CHOICE",
        label: "Choice"
    }, { value: "COMMENT", label: "Comment" }];
    let typeKey = question.type ? question.type : EMPTY_VALUE;
    let typeVal = _.find(typeOptions, function (to) {
        return to.value === typeKey;
    })

    const subjectRoleOptions = [{ value: "ALL", label: "ALL" }, { value: "CALLTAKER", label: "Call Taker" }, { value: "RESPONDER", label: "Responder" }];
    let subjectRoleKey = question.subjectRole ? question.subjectRole : null;
    let subjectRoleVal = _.find(subjectRoleOptions, function (sro) {
        return sro.value === subjectRoleKey;
    })

    let body = null;
    // eslint-disable-next-line default-case
    switch (typeKey) {
        case "COMMENT":
            body = null;
            break;
        case "SCALE":
            body = (
                <div className={"scaleRange questionItem"}>
                    <div className={"numberInput"}>
                        <InputCell
                            defaultValue={(question.low ? question.low : "")}
                            required
                            disabled={disabled}
                            type={"number"}
                            onChange={onInputChange.bind(this, "low")}
                            value={question.low}
                        />
                    </div>

                    <InputCell
                        defaultValue={(question.lowDescription ? question.lowDescription : "")}
                        disabled={disabled}
                        onChange={onInputChange.bind(this, "lowDescription")}
                        value={question.lowDescription}
                        placeholder={"low value label"}
                    />
                    <span className={"scaleRangeTo"}>to</span>
                    <div className={"numberInput"}>
                        <InputCell
                            defaultValue={(question.high ? question.high : "")}
                            required
                            disabled={disabled}
                            type={"number"}
                            onChange={onInputChange.bind(this, "high")}
                            value={question.high}
                        />
                    </div>
                    <InputCell
                        defaultValue={(question.highDescription ? question.highDescription : "")}
                        disabled={disabled}
                        onChange={onInputChange.bind(this, "highDescription")}
                        value={question.highDescription}
                        placeholder={"high value label"}
                    />
                </div>
            )
            break;
        case "CHOICE":
            body = (
                <div className="multipleChoice">
                    {
                        _.map(_.sortBy(question.options, function(oo){
                            return oo.optionOrder;
                        }), function (opt, j) {
                            return (
                                <div key={'option-' + j} className={"questionItem multipleChoiceItem"}>
                                    <InputCell
                                        defaultValue={(opt.option ? opt.option : "")}
                                        wrapperClasses={"plain"}
                                        placeholder={"option text (e.g. Very Satisfied)"}
                                        required
                                        disabled={disabled}
                                        type={"text"}
                                        onChange={onOptChange.bind(this, j)}
                                    />
                                    <InputCell
                                        defaultValue={(opt.shortOption ? opt.shortOption : "")}
                                        style={{ width: '80%' }}
                                        wrapperClasses={"plain multiChoiceShortcut"}
                                        placeholder={"shortcut (e.g. V)"}
                                        required
                                        disabled={disabled}
                                        type={"text"}
                                        onChange={onShortOptChange.bind(this, j)}
                                    />
                                    <div className={"remove"} onClick={removeOpt.bind(this, j)}>
                                        <div className="icon" />
                                    </div>
                                </div>
                            )
                        })
                    }
                    <div onClick={addOpt} className={"questionItem button large action addOption"}>
                        <div className="title">Add another option...</div>
                    </div>
                </div>
            );
            break;
    }


    const generateCompValue = (criteria, question, idx) => {
        let compValueIn = null;
        if (criteria != null) {
            if (isExpandedSentimentTypesEnabled() && criteria.comparisonField === "SENTIMENT") {
                if (criteria.comparison === "IN_LIST" || criteria.comparison === "NOT_IN_LIST") {
                    const multiMenuKey = `multi_${idx}`;
                    compValueIn =
                        <Select
                            className="tableCell selectCell sentimentValueCell"
                            classNamePrefix="selectCell"
                            isMulti
                            isClearable
                            isDisabled={disabled}
                            options={sentimentDropdownOptions}
                            value={multiSelectValue[idx] || []}
                            menuIsOpen={!!sentimentMenuOpen[multiMenuKey]}
                            onMenuOpen={() => setSentimentMenuOpen(prev => ({ ...prev, [multiMenuKey]: true }))}
                            onMenuClose={() => setSentimentMenuOpen(prev => ({ ...prev, [multiMenuKey]: false }))}
                            onChange={(val) => {
                                const newMultiSelect = [...multiSelectValue];
                                newMultiSelect[idx] = val || [];
                                setMultiSelectValue(newMultiSelect);
                                const newCriterias = [...reviewCriterias];
                                newCriterias[idx].comparisonValue = (val || []).map(o => o.value).join(",");
                                setReviewCriterias(newCriterias);
                                question.surveyQuestionReviewCriteria = newCriterias;
                                onUpdate(question);
                            }}
                            styles={sentimentMultiSelectStyles}
                            components={{ Option: SentimentCheckboxOption, ValueContainer: SentimentValueContainer, ClearIndicator: SentimentClearIndicator }}
                            hideSelectedOptions={false}
                            closeMenuOnSelect={false}
                            aria-label="Select sentiments"
                            ariaLiveMessages={sentimentAriaLiveMessages}
                            tabSelectsValue={false}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab' && sentimentMenuOpen[multiMenuKey]) {
                                    setSentimentMenuOpen(prev => ({ ...prev, [multiMenuKey]: false }));
                                    e.preventDefault();
                                } else if (e.key === 'Enter' && !sentimentMenuOpen[multiMenuKey]) {
                                    setSentimentMenuOpen(prev => ({ ...prev, [multiMenuKey]: true }));
                                    e.preventDefault();
                                }
                            }}
                            placeholder=""
                        />;
                } else if (criteria.comparison === "EQUAL") {
                    const eqMenuKey = `eq_${idx}`;
                    const currentVal = reviewCriterias[idx].comparisonValue
                        ? sentimentDropdownOptions.find(o => o.value === reviewCriterias[idx].comparisonValue.toUpperCase()) || null
                        : null;
                    compValueIn =
                        <Select
                            className="tableCell selectCell sentimentValueCell"
                            classNamePrefix="selectCell"
                            isDisabled={disabled}
                            options={sentimentDropdownOptions}
                            value={currentVal}
                            menuIsOpen={!!sentimentMenuOpen[eqMenuKey]}
                            onMenuOpen={() => setSentimentMenuOpen(prev => ({ ...prev, [eqMenuKey]: true }))}
                            onMenuClose={() => setSentimentMenuOpen(prev => ({ ...prev, [eqMenuKey]: false }))}
                            onChange={(opt) => {
                                onReviewSelectValueChange(idx, opt);
                                setSentimentMenuOpen(prev => ({ ...prev, [eqMenuKey]: false }));
                            }}
                            aria-label="Select sentiment"
                            ariaLiveMessages={sentimentAriaLiveMessages}
                            tabSelectsValue={false}
                            onKeyDown={(e) => {
                                if (e.key === 'Tab' && sentimentMenuOpen[eqMenuKey]) {
                                    setSentimentMenuOpen(prev => ({ ...prev, [eqMenuKey]: false }));
                                    e.preventDefault();
                                } else if (e.key === 'Enter' && !sentimentMenuOpen[eqMenuKey]) {
                                    setSentimentMenuOpen(prev => ({ ...prev, [eqMenuKey]: true }));
                                    e.preventDefault();
                                }
                            }}
                            placeholder=""
                        />;
                } else {
                    compValueIn =
                        <InputCell
                            wrapperClasses="tableCell valueCell"
                            disabled={disabled}
                            placeholder={"Value"}
                            type="text"
                            onChange={onReviewValueChange.bind(this, idx)}
                            defaultValue={criteria ? reviewCriterias[idx].comparisonValue : ""}
                            inputProps={{ 'aria-label': 'Sentiment comparison value' }}
                        />;
                }
            } else if (question.type === "CHOICE") {
                let choiceOptions = _.map(question.options, function (opt) {
                    return { label: opt.option, value: opt.option }
                })
                if (criteria.comparison === "IN_LIST" || criteria.comparison === "NOT_IN_LIST") {
                    compValueIn =
                        <CreatableSelect
                            className="tableCell selectCell"
                            components={components}
                            inputValue={inputValue[idx]}
                            isClearable
                            isMulti
                            onChange={handleChange.bind(this, idx)}
                            onInputChange={handleInputChange.bind(this, idx)}
                            onKeyDown={handleKeyDown.bind(this, idx)}
                            placeholder=""
                            value={multiSelectValue[idx]}
                            options={choiceOptions}
                        />
                } else {
                    compValueIn =
                        <Select className="tableCell selectCell"
                            styles={{menuPortal: base => ({...base, zIndex: 9999}), menu: base => ({...base, color: 'black'})}}
                            isDisabled={disabled}
                            value={criteria ? {
                                label: reviewCriterias[idx].comparisonValue,
                                value: reviewCriterias[idx].comparisonValue
                            } : ""}
                            onChange={onReviewSelectValueChange.bind(this, idx)}
                            options={choiceOptions} />
                }
            } else {
                if (criteria.comparison === "IN_LIST" || criteria.comparison === "NOT_IN_LIST") {
                    compValueIn =
                        <CreatableSelect
                            className="tableCell selectCell"
                            components={components}
                            inputValue={inputValue[idx]}
                            isClearable
                            isMulti
                            menuIsOpen={false}
                            onChange={handleChange.bind(this, idx)}
                            onInputChange={handleInputChange.bind(this, idx)}
                            onKeyDown={handleKeyDown.bind(this, idx)}
                            placeholder=""
                            value={multiSelectValue[idx]}
                        />
                } else {
                    compValueIn =
                        <InputCell
                            wrapperClasses="tableCell valueCell"
                            disabled={disabled}
                            placeholder={"Value"}
                            type={question.type === "SCALE" ? "number" : "text"}
                            onChange={onReviewValueChange.bind(this, idx)}
                            defaultValue={criteria ? reviewCriterias[idx].comparisonValue : ""}
                        />
                }
            }
        }
        return compValueIn;
    }

    let surveyReviewSection = null;
    let taskOpts = _.concat([], _.map(taskTypes, function (tt) { return { value: tt.id, label: tt.name, data: tt } }));

    if (user != null && user.features != null) {
        surveyReviewSection = (
            <div>
                {
                    _.map(reviewCriterias, function (c, j) {
                        return (
                            <div className="questionCriteria">
                                <div className="criteriaContainer">
                                    <div className="criteriaRow filterRow">
                                        <div className="tableCell titledSelectCell conditionCell">
                                            <div className="title">If...</div>
                                            <Select className="selectCell" classNamePrefix="selectCell"
                                                isDisabled={disabled}
                                                aria-label="Criteria condition field"
                                                value={c.comparisonField ? _.find(surveyActionComparisonFields, function (sacf) {
                                                    return sacf.value === c.comparisonField;
                                                }) : ""} onChange={setQuestionReviewCriteriaComparisonField.bind(this, j)}
                                                options={comparisonFieldOpts(question.type)} />
                                        </div>

                                        <Select className="tableCell selectCell operatorCell" classNamePrefix="selectCell"
                                            isDisabled={disabled}
                                            aria-label="Criteria comparison operator"
                                            value={c.comparison ? _.find(comparisonOperators, function (co) {
                                                return co.value === c.comparison;
                                            }) : ""} onChange={setQuestionReviewCriteriaComparison.bind(this, j)}
                                            options={comparisonOpts(question.type)} />
                                        {generateCompValue(c, question, j)}
                                        {isExpandedSentimentTypesEnabled() && c.comparisonField === "SENTIMENT" ? (
                                            <FormControlLabel
                                                style={{ margin: 0, marginRight: 4, whiteSpace: 'nowrap', flexShrink: 0 }}
                                                label={
                                                    <span style={{ display: "flex", alignItems: "center",marginLeft: '4px', gap: 2, fontSize: 14 }}>
                                                        Noteworthy Feedback
                                                        <Tooltip title="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions.">
                                                            <button
                                                                type="button"
                                                                aria-roledescription="note"
                                                                aria-label="When the Noteworthy Feedback checkbox is selected, the chosen sentiment(s) will trigger the actions only if the Feedback is also marked as Noteworthy. If the Noteworthy Feedback checkbox is not selected, all Feedback matching the chosen sentiment(s) will trigger the actions."
                                                                style={{ background: 'none', border: 'none', padding: 0, cursor: 'help', display: 'flex', color: 'inherit', lineHeight: 0 }}
                                                            >
                                                                <InfoOutlinedIcon style={{ fontSize: 14 }} aria-hidden={true} />
                                                            </button>
                                                        </Tooltip>
                                                    </span>
                                                }
                                                control={
                                                    <Checkbox
                                                        disabled={disabled}
                                                        checked={!!c.noteworthy}
                                                        onChange={(e) => handleNoteworthyChange(j, e.target.checked)}
                                                        size="small"
                                                        style={{ padding: 2 }}
                                                        disableRipple
                                                        inputProps={{ 'aria-label': 'Noteworthy Feedback' }}
                                                        sx={{
                                                            color: 'var(--colorTitle)',
                                                            '&.Mui-checked': { color: 'var(--colorTitle)' },
                                                            '&.Mui-focusVisible': { outline: '2px solid var(--colorTitle)', outlineOffset: 1 },
                                                        }}
                                                    />
                                                }
                                            />
                                        ) : null}
                                    </div>

                                    <div className="criteriaRow actionRow">
                                        <div className="tableCell titledSelectCell">
                                            <div className="title">Then...</div>
                                            <Select className="selectCell" classNamePrefix="selectCell"
                                                isDisabled={disabled}
                                                value={c && c.targetType ? _.find(surveyReviewTargetTypeOptionsWithoutQuestion, function (tt) {
                                                    return tt.value === c.targetType;
                                                }) : ""} onChange={setQuestionReviewCriteriaTargetType.bind(this, j)}
                                                options={surveyReviewTargetTypeOptionsWithoutQuestion} />
                                        </div>

                                        {c && c.targetType === "CREATE_TASK" ?
                                            <div className="tableCell titledSelectCell">
                                                <div className="title">Task Type</div>
                                                <Select className="selectCell" classNamePrefix="selectCell"
                                                    isDisabled={disabled} value={c.taskType ? { label: c.taskType.name, value: c.taskType.id } : ""} onChange={setTargetTaskType.bind(this, j)}
                                                    options={taskOpts} />
                                            </div>
                                            : null}
                                    </div>
                                </div>

                                <div className={"removeCriteria"} onClick={removeCriteria.bind(this, j)}>
                                    <div className="icon">
                                        <div className="iconLine lineVertical" />
                                        <div className="iconLine lineHorizontal" />
                                    </div>
                                </div>

                            </div>
                        )
                    }
                    )}
                {disabled ? null : (
                    <div onClick={addCriteria} className="button action medium addCriteria">
                        <div className="title">Add Criteria</div>
                    </div>)}
            </div>
        )
    }

    return (
        <div>
            <div className="content">
                <div className="tableView">
                    <div className="tableSection sectionSurveyQuestions">
                        <div>
                            <InputCell
                                defaultValue={(question.name ? question.name : "")}
                                disabled={disabled}
                                onChange={onNameChange}
                                value={question.name}
                                placeholder={"Name"}
                            />
                        </div>
                        <div className={"questionContainer questionCell"}>
                            <div className={"questionBody"}>
                                {
                                    typeKey === EMPTY_VALUE ? (<span className={"questionEmptyState"}>Select a question type to start editing</span>)
                                        : (
                                            <div className={"questionTitle tableCell editorCell"} onClick={editorContainerClick}>
                                                <Editor
                                                    readOnly={disabled}
                                                    style={{ height: "100%" }}
                                                    editorState={editorState}
                                                    onChange={updateEditorState}
                                                    onBlur={blurEditor}
                                                    placeholder={"Click and type to survey question prompt..."}
                                                    spellCheck={true}
                                                    stripPastedStyles={true}
                                                    ref={setDomEditorRef}
                                                />
                                            </div>
                                        )
                                }
                                <div className="tableCell titledSelectCell targetResponseGroup">
                                    <div className="title">Subject Role</div>
                                    <Select placeholder={"Subject Role"} className="questionType tableCell selectCell" classNamePrefix="selectCell" isDisabled={disabled} value={subjectRoleVal} onChange={setQuestionSubjectRole} options={subjectRoleOptions} />
                                </div>
                                {body}
                                {surveyReviewSection}

                            </div>

                            <Select className="questionType tableCell selectCell" classNamePrefix="selectCell" isDisabled={existingQuestion ? true : false} value={typeVal} onChange={setQuestionType} options={typeOptions} />

                        </div>
                    </div>
                </div>
            </div>

            <div className="actionBar">
                <div className="actions">
                    <div onClick={onCancel} className="button dismiss medium">
                        <div className="title">Cancel</div>
                    </div>

                    <div onClick={saveQuestion} className={`button confirm medium`}>
                        <div className="title">Save</div>
                    </div>
                </div>

                <div className="separator"></div>
            </div>
        </div>
    );
}